import { FormEvent, useState, useContext } from "react";

import * as S from "./styles";
import ArrowListIcon from "../../../assets/icons/ArrowListIcon";
import states from "../../../mocks/states";
import axios from "axios";
import SearchIcon from "../../../assets/icons/SearchIcon";
import { GlobalContext } from "../../../context/globalContext";
import currencyFormatter from "../../../utils/currencyFormatter";
import IneligibleScreen from "../../IneligibleScreen";
import { formatNumberToBRL } from "../../../utils/formatInputNumberToBrl";
import { ButtonPrimary } from "@telefonica/mistica";
import { api } from "../../../lib/api";

type TCitiesList = Array<{ city: string; id: number }>;

// TODO: mover para o arquivo adequado
declare global {
  interface Window {
    dataLayer: Array<
      Partial<
        | {
            event: string;
            tipoLead: string | null;
            consumo: number;
            tel: string;
            email: string;
          }
        | {
            event: string;
          }
      >
    >;
  }
}

const EconomyCalculation = () => {
  const {
    showIneligibleScreen,
    selectedCity,
    selectedState,
    monthlyValue,
    setSelectedCity,
    setSelectedState,
    setMonthlyValue,
    setShowSimulationResult,
    setResultInformations,
    setShowIneligibleScreen,
  } = useContext(GlobalContext);

  const [openStatesList, setOpenStatesList] = useState(false);
  const [monthlyValueError, setMonthlyValueError] = useState(false);
  const [stateError, setStateError] = useState(false);
  const [cityError, setCityError] = useState(false);

  const [openCitiesList, setOpenCitiesList] = useState(false);
  const [citiesList, setCitiesList] = useState<TCitiesList>([]);

  const [searchForACity, setSearchForACity] = useState("");
  const [loadingSimulation, setLoadingSimulation] = useState(false);
  const [apiError, setApiError] = useState("");

  let displayCities = [] as TCitiesList;

  function removeSpecialCharactersToFilter(value: string) {
    const valueFormatted = value
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replaceAll(" ", "")
      .toLowerCase();

    return valueFormatted;
  }

  if (searchForACity.length) {
    displayCities = citiesList.filter((cities) => {
      const cityValueWritten = removeSpecialCharactersToFilter(searchForACity);

      const cityToGet = removeSpecialCharactersToFilter(cities.city);

      return cityToGet.includes(cityValueWritten);
    });
  } else {
    displayCities = citiesList.sort((a, b) => a.city.localeCompare(b.city));
  }

  const formatCalculationValue = monthlyValue
    ?.replaceAll(".", "")
    .replaceAll(",", ".");

  async function handleGetCitiesFromSelectedState(state: string) {
    setOpenCitiesList(false);
    setCitiesList([]);
    setSelectedCity(null);

    const url = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state}/municipios`;

    try {
      const stateResponse = await axios.get(url);

      const cities = stateResponse.data.map(
        (city: { nome: string; id: number }) => {
          return { city: city.nome, id: city.id };
        }
      );

      setCitiesList(cities);
    } catch (e) {
      console.log("Ocorreu um erro ao buscar pelas cidades...");
    }
  }

  function handleShowInelegibleScreen() {
    setShowIneligibleScreen(true);
    setLoadingSimulation(false);
    setSelectedState("");
  }

  async function handleSendSimulation(e: FormEvent) {
    e.preventDefault();

    if (!selectedState) {
      setStateError(true);
    }

    if (!selectedCity) {
      setCityError(true);
    }

    if (+formatCalculationValue <= 0) {
      return setMonthlyValueError(true);
    }

    if (!selectedCity?.id) return;

    setLoadingSimulation(true);

    const inputElement = document.querySelector<HTMLInputElement>(
      "#campoContaMensal > input"
    );

    if (inputElement && inputElement.value) {
      const consumo = +inputElement.value.split(",")[0].replace(".", "");

      let tipoLead = "";

      if (consumo >= 10000 && consumo <= 200000) {
        tipoLead = "lead_qualificado";
      } else {
        tipoLead = "lead_nao_qualificado";
      }

      localStorage.setItem("tipoLead", tipoLead);
      localStorage.setItem("consumo", consumo.toString());

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "firstForm_conversion",
        tipoLead: tipoLead,
        consumo: consumo,
        email: "",
        tel: "",
      });
    }

    try {
      const { data: discountResponse } = await api.post("/discount", {
        ibge: selectedCity.id.toString(),
        faturaDigitada: formatCalculationValue,
      });

      if (discountResponse) {
        // A API retorna dados em discountResponse.dados
        const dados = discountResponse.dados || discountResponse;
        console.log("Discount response:", discountResponse);
        console.log("Dados extraídos:", dados);
        const descontoFinanceiroGud =
          dados.descontoFinanceiroGud ||
          dados.desconto_financeiro_gud ||
          dados.valor ||
          0;
        const descontoEmReais = Number(descontoFinanceiroGud);
        console.log("Desconto em reais:", descontoEmReais);
        const formattedDiscount = currencyFormatter(descontoEmReais.toString());
        const formattedPercentage = String(
          Number(
            (dados.descontoPercentual || dados.desconto_percentual || 0) * 100
          ).toFixed(0)
        );
        console.log("Percentual formatado:", formattedPercentage);

        setResultInformations({
          economy: formattedDiscount,
          percentage: formattedPercentage,
          city: selectedCity.city,
          cityId: selectedCity.id,
          monthlyValue: formatCalculationValue,
        });

        if (Number(formatCalculationValue) < 5000) {
          handleShowInelegibleScreen();
          return;
        }

        setShowSimulationResult(true);
        window.scrollTo({
          top: window.innerWidth ? 2415 : 1460,
          left: 0,
          behavior: "smooth",
        });
      } else {
        throw {
          error:
            discountResponse?.mensagem_usuario || "Erro ao calcular desconto",
        };
      }
    } catch (e) {
      const errorMsg = e as { error?: string; message?: string };
      const errorText =
        errorMsg?.error || errorMsg?.message || "Erro ao calcular desconto";
      setApiError(errorText);
    } finally {
      setLoadingSimulation(false);
    }
  }

  return (
    <>
      {!showIneligibleScreen ? (
        <S.CalculationRoot>
          <S.CalculationWrapper>
            <S.InfoCol>
              {import.meta.env.VITE_APP_BLACK_FRIDAY === "true" ? (
                <>
                  <S.MainHeader>
                    BLACK GUD: Condições imperdíveis por tempo limitado.
                  </S.MainHeader>

                  <S.HeaderText>
                    Venha para o Mercado Livre de Energia com ainda mais
                    economia e tire a sua empresa do escuro.
                    <a
                      href="https://image.marketing.aurenenergia.com.br/lib/fe3915707564047f7d1778/m/1/b47039d7-7f58-453b-ad48-95bb6362a275.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {" "}
                      Consulte regulamento
                    </a>
                    .
                  </S.HeaderText>
                </>
              ) : (
                <>
                  <S.MainHeader>
                    Descubra uma nova forma de consumir energia!
                  </S.MainHeader>

                  <S.HeaderText>
                    Venha para o Mercado Livre de Energia com a GUD, e aproveite
                    benefícios que vão muito além da economia!
                    <br />
                    <br />
                    Conte com a solidez de <strong>Auren e Vivo</strong> para
                    ajudar a potencializar sua empresa!
                  </S.HeaderText>
                </>
              )}
            </S.InfoCol>
            <S.CalculationCol>
              <S.CalculationForm onSubmit={handleSendSimulation}>
                <h2>
                  Saiba se sua empresa já pode fazer parte do Mercado Livre de
                  Energia.
                  <br /> Preencha o formulário abaixo:
                </h2>
                <S.InputsWrapper>
                  <div className="labelDiv">
                    Minha empresa está localizada em:
                    <S.InputSection onClick={(e) => e.stopPropagation()}>
                      <S.TriggerWrapper
                        id="campoEstado"
                        className="state-field"
                        $floatText={Boolean(selectedState)}
                        $changeColor={Boolean(selectedState)}
                      >
                        <button
                          className={openStatesList ? "openedList" : ""}
                          type="button"
                          onClick={() => {
                            setOpenStatesList(!openStatesList);
                            setOpenCitiesList(false);
                          }}
                        >
                          <p className="miniText">Estado (UF)</p>
                          {selectedState} <ArrowListIcon />
                        </button>
                        <S.List $openList={openStatesList}>
                          {states.map((state) => {
                            return (
                              <li
                                onClick={() => {
                                  setStateError(false);
                                  setSelectedState(state);
                                  setOpenStatesList(false);
                                  handleGetCitiesFromSelectedState(state);
                                }}
                                key={state}
                              >
                                {state}
                              </li>
                            );
                          })}
                        </S.List>
                        <S.InputError>
                          {stateError && "Obrigatório"}
                        </S.InputError>
                      </S.TriggerWrapper>

                      <S.TriggerWrapper
                        id="campoMunicipio"
                        className="city-field"
                        $floatText={Boolean(selectedCity)}
                        $changeColor={Boolean(selectedCity)}
                      >
                        <button
                          disabled={!citiesList.length}
                          className={openCitiesList ? "openedList" : ""}
                          type="button"
                          onClick={() => {
                            setOpenCitiesList(!openCitiesList);
                            setOpenStatesList(false);
                          }}
                        >
                          <p className="miniText">Município</p>
                          {selectedCity?.city} <ArrowListIcon />
                        </button>
                        <S.List
                          onClick={(e) => e.stopPropagation()}
                          $openList={openCitiesList}
                        >
                          <S.SearchCityInput>
                            <input
                              type="text"
                              onChange={({ target }) =>
                                setSearchForACity(target.value)
                              }
                            />
                            <SearchIcon />
                          </S.SearchCityInput>
                          {displayCities.map(({ city, id }) => {
                            return (
                              <li
                                key={id}
                                onClick={() => {
                                  setCityError(false);

                                  setSelectedCity({ city, id });
                                  setOpenCitiesList(false);
                                }}
                              >
                                {city}
                              </li>
                            );
                          })}
                        </S.List>
                        <S.InputError>
                          {cityError && "Obrigatório"}
                        </S.InputError>
                      </S.TriggerWrapper>
                    </S.InputSection>
                  </div>

                  <div className="labelDiv">
                    Com uma conta de energia mensal de:
                    <S.FloatingInput
                      id="campoContaMensal"
                      $floatTextError={monthlyValueError}
                      $floatText={Boolean(monthlyValue?.length)}
                    >
                      <input
                        value={monthlyValue}
                        type="text"
                        onChange={(e) => {
                          setMonthlyValueError(false);
                          formatNumberToBRL(e, setMonthlyValue);
                        }}
                      />
                      <p className="miniText">Consumo em R$</p>
                    </S.FloatingInput>
                    <S.InputError>
                      {monthlyValueError &&
                        (!monthlyValue ? "Obrigatório" : "Valor inválido")}
                    </S.InputError>
                  </div>
                </S.InputsWrapper>
                {apiError && (
                  <S.ApiError>
                    <p>{apiError}</p>
                  </S.ApiError>
                )}
                <S.SimulateButton
                  id="btnQueroEconomizar"
                  $formLoading={loadingSimulation}
                >
                  <ButtonPrimary
                    loadingText="Calculando..."
                    submit
                    showSpinner={loadingSimulation}
                    className={
                      !monthlyValueError &&
                      !stateError &&
                      !cityError &&
                      selectedCity?.id &&
                      selectedState &&
                      monthlyValue
                        ? "firstForm-conversion"
                        : ""
                    }
                  >
                    Descubra se a empresa pode participar.
                  </ButtonPrimary>
                </S.SimulateButton>
              </S.CalculationForm>
            </S.CalculationCol>
          </S.CalculationWrapper>
        </S.CalculationRoot>
      ) : (
        <IneligibleScreen />
      )}
      <span className="economySimulation" />
    </>
  );
};

export default EconomyCalculation;
